import {isFeatureEnabled} from 'feature-flags';

import {Module, Permission} from '@dms/api/shared';
import {moduleKeys} from '@dms/config';
import featureFlags from '@dms/feature-flags';
import {interestRoutes, workshopRoutes} from '@dms/routes';
import {permissions} from '@dms/shared';

import {buildArray} from 'shared/web';

import {InterestCreate} from './pages/InterestCreate/InterestCreate';
import {InterestDetail} from './pages/InterestDetail/InterestDetail';
import {InterestOverview} from './pages/InterestOverview/InterestOverview';
import {WorkshopInterest} from './pages/WorkshopInterest/WorkshopInterest';
import {WorkshopInterestCreate} from './pages/WorkshopInterestCreate/WorkshopInterestCreate';
import {WorkshopInterestDetail} from './pages/WorkshopInterestDetail/WorkshopInterestDetail';

export function useInterestModule(): Module {
  const isWorkshopInterestEnabled = isFeatureEnabled(featureFlags.WORKSHOP_INTEREST);

  return {
    id: moduleKeys.interest,
    layoutProps: {
      title: 'page.interests.title',
      icon: 'sidebar/interest',
    },
    routerProps: {
      path: interestRoutes.overview,
      element: <InterestOverview />,
    },
    requiredPermissionAtLeastOne: buildArray<Permission>([permissions.businessCaseRead])
      .when(isWorkshopInterestEnabled, permissions.workshopInterestRead)
      .when(isFeatureEnabled(featureFlags.INTEREST_RECOMMENDATIONS), permissions.interestRead),
    content: [
      {
        id: 'interest-my-day',
        routerProps: {
          path: interestRoutes.myDay,
          element: <InterestOverview />,
        },
        layoutProps: {title: 'page.interestMyDay.header.title', isPartOfCreateMenuItem: false},
        requiredPermission: permissions.interestRead,
        requiredFeatureFlag: featureFlags.INTEREST_RECOMMENDATIONS,
      },
      {
        id: 'workshop-interest',
        routerProps: {
          path: workshopRoutes.interest,
          element: <WorkshopInterest />,
        },
        requiredPermission: permissions.workshopInterestRead,
        requiredFeatureFlag: featureFlags.WORKSHOP_INTEREST,
      },
      {
        id: 'workshop-interestCreate',
        routerProps: {
          path: workshopRoutes.interestCreate,
          element: <WorkshopInterestCreate />,
        },
        requiredPermission: permissions.workshopInterestCreate,
        requiredFeatureFlag: featureFlags.WORKSHOP_INTEREST,
      },
      {
        id: 'workshop-interestDetail',
        routerProps: {
          paths: [
            workshopRoutes.interestDetailOverview,
            workshopRoutes.interestDetailCustomer,
            workshopRoutes.interestDetailOwnership,
            workshopRoutes.interestDetailServiceRequest,
            workshopRoutes.interestDetailChecklist,
            workshopRoutes.interestDetailTasks,
            workshopRoutes.interestDetailPostponedRequests,
            workshopRoutes.interestDetailActiveServiceCases,
            workshopRoutes.interestDetailHistoryOfRepairs,
            workshopRoutes.interestDetailServiceCampaigns,
            workshopRoutes.interestDetailTires,
            workshopRoutes.interestDetailActionLog,
            workshopRoutes.interestDetailCommunication,
          ],
          element: <WorkshopInterestDetail />,
        },
        requiredPermission: permissions.workshopInterestRead,
        requiredFeatureFlag: featureFlags.WORKSHOP_INTEREST,
      },
      {
        id: 'workshop-interestDetailCustomNotices',
        routerProps: {
          path: workshopRoutes.interestDetailCustomNotices,
          element: <WorkshopInterestDetail />,
        },
        requiredPermission: permissions.workshopInterestRead,
        requiredFeatureFlag: (isFeatureEnabled) =>
          isFeatureEnabled(featureFlags.WORKSHOP_INTEREST) &&
          isFeatureEnabled(featureFlags.CUSTOM_NOTICES),
      },
      {
        id: 'interest-overview',
        routerProps: {
          path: interestRoutes.overview,
          element: <InterestOverview />,
        },
      },
      {
        id: 'interest-workshop-overview',
        routerProps: {
          path: interestRoutes.workshopOverview,
          element: <InterestOverview />,
        },
      },
      {
        id: 'interest-detail',
        routerProps: {
          paths: [
            interestRoutes.detailOverview,
            interestRoutes.detailCustomer,
            interestRoutes.detailSelling,
            interestRoutes.detailBuying,
            interestRoutes.detailChecklists,
            interestRoutes.detailTasks,
            interestRoutes.detailDocuments,
            interestRoutes.detailActivityLog,
            interestRoutes.detailCommunication,
          ],
          element: <InterestDetail />,
        },
      },
      {
        id: 'interest-detail-customNotices',
        requiredFeatureFlag: featureFlags.CUSTOM_NOTICES,
        routerProps: {
          path: interestRoutes.detailCustomNotices,
          element: <InterestDetail />,
        },
      },
      {
        id: 'interest-create',
        routerProps: {
          path: interestRoutes.create,
          element: <InterestCreate />,
        },
        requiredPermission: permissions.interestCreate,
        layoutProps: {
          title: 'entity.interest.actions.newInterest',
          isPartOfCreateMenuItem: true,
        },
      },
    ],
  };
}
